import { formatDistanceToNow } from 'date-fns';
import {
  hasScheduledFollowUp,
  isLeadStaleNoActivity,
  resolveLastActivityAt,
  type CrmNoActivitySettings,
} from './noActivitySettings';

/** Minimal opportunity fields for required-actions list. */
export interface CrmRequiredActionOpportunity {
  id: number;
  status: string;
  title: string;
  customer_id?: string;
  customer_name?: string;
  created_at: string;
  is_unread?: boolean;
  next_follow_up_at?: string | null;
  last_activity_at?: string | null;
  /** Null until Sales has scored this lead. */
  lead_score?: number | null;
  lead_score_reason?: string | null;
}

export type CrmRequiredActionKind =
  | 'new_lead'
  | 'follow_up_due'
  | 'no_activity'
  | 'proposal_convert'
  | 'payroll_request'
  | 'payroll_period'
  | 'document_verification'
  | 'campaign_billing_failed'
  | 'marketing_ai_tokens_blocked'
  | 'agent_callback'
  | 'due_task'
  | 'order_approval'
  | 'sales_play';

export interface CrmRequiredActionItem {
  kind: CrmRequiredActionKind;
  opportunityId?: number;
  proposalId?: string;
  payrollRequestId?: string;
  payrollPeriodStart?: string;
  payrollPeriodEnd?: string;
  payrollPeriodDaysPastDue?: number;
  documentSubmissionId?: string;
  campaignId?: string;
  callbackId?: string;
  todoId?: string;
  /** order_approval only: the customer_orders row awaiting approval. */
  orderId?: string;
  /** sales_play only: a lead whose only reason to be here is a staged Sales play. */
  salesPlayId?: string;
  /** due_task only: past its due moment (vs due later today). */
  todoOverdue?: boolean;
  title: string;
  customerName: string;
  subtitle: string;
  /** Sales' 0–100 read of how likely this lead is to book. Absent until set. */
  leadScore?: number | null;
  leadScoreReason?: string | null;
  /** Lower sorts first (more urgent). */
  sortOrder: number;
  dueAt?: string;
  createdAt: string;
  lastActivityAt?: string;
  /** Completed deposit total for approved proposals (when a deposit was taken). */
  depositAmount?: number;
}

export interface CrmRequiredActionsGrouped {
  followUpDue: CrmRequiredActionItem[];
  newLeads: CrmRequiredActionItem[];
  noActivity: CrmRequiredActionItem[];
  proposalConvert: CrmRequiredActionItem[];
  payrollRequests: CrmRequiredActionItem[];
  payrollPeriods: CrmRequiredActionItem[];
  documentVerifications: CrmRequiredActionItem[];
  campaignBillingFailed: CrmRequiredActionItem[];
  marketingAiTokensBlocked: CrmRequiredActionItem[];
  agentCallbacks: CrmRequiredActionItem[];
  dueTasks: CrmRequiredActionItem[];
  orderApprovals: CrmRequiredActionItem[];
  salesPlays: CrmRequiredActionItem[];
  total: number;
}

export function mergeRequiredActions(
  crm: Pick<CrmRequiredActionsGrouped, 'followUpDue' | 'newLeads' | 'noActivity'>,
  extras: {
    proposalConvert?: CrmRequiredActionItem[];
    payrollRequests?: CrmRequiredActionItem[];
    payrollPeriods?: CrmRequiredActionItem[];
    documentVerifications?: CrmRequiredActionItem[];
    campaignBillingFailed?: CrmRequiredActionItem[];
    marketingAiTokensBlocked?: CrmRequiredActionItem[];
    agentCallbacks?: CrmRequiredActionItem[];
    dueTasks?: CrmRequiredActionItem[];
    orderApprovals?: CrmRequiredActionItem[];
    salesPlays?: CrmRequiredActionItem[];
  } = {},
): CrmRequiredActionsGrouped {
  const proposalConvert = extras.proposalConvert ?? [];
  const payrollRequests = extras.payrollRequests ?? [];
  const payrollPeriods = extras.payrollPeriods ?? [];
  const documentVerifications = extras.documentVerifications ?? [];
  const campaignBillingFailed = extras.campaignBillingFailed ?? [];
  const marketingAiTokensBlocked = extras.marketingAiTokensBlocked ?? [];
  const agentCallbacks = extras.agentCallbacks ?? [];
  const dueTasks = extras.dueTasks ?? [];
  const orderApprovals = extras.orderApprovals ?? [];
  const salesPlays = extras.salesPlays ?? [];
  return {
    followUpDue: crm.followUpDue,
    newLeads: crm.newLeads,
    noActivity: crm.noActivity,
    proposalConvert,
    payrollRequests,
    payrollPeriods,
    documentVerifications,
    campaignBillingFailed,
    marketingAiTokensBlocked,
    agentCallbacks,
    dueTasks,
    orderApprovals,
    salesPlays,
    total:
      crm.followUpDue.length +
      crm.newLeads.length +
      crm.noActivity.length +
      proposalConvert.length +
      payrollRequests.length +
      payrollPeriods.length +
      documentVerifications.length +
      campaignBillingFailed.length +
      marketingAiTokensBlocked.length +
      agentCallbacks.length +
      dueTasks.length +
      orderApprovals.length +
      salesPlays.length,
  };
}

/** Response-time urgency for unread new leads (by age since created). */
export type NewLeadResponseSeverity = 'none' | 'warning' | 'critical';

const MS_PER_HOUR = 60 * 60 * 1000;

/**
 * New lead age badges:
 * - under 2 hours: none
 * - 2–4 hours: warning (yellow)
 * - 4+ hours: critical (red)
 */
export function getNewLeadResponseSeverity(
  createdAt: string,
  now: Date = new Date(),
): NewLeadResponseSeverity {
  const createdMs = new Date(createdAt).getTime();
  if (Number.isNaN(createdMs)) return 'none';

  const ageHours = (now.getTime() - createdMs) / MS_PER_HOUR;
  if (ageHours >= 4) return 'critical';
  if (ageHours >= 2) return 'warning';
  return 'none';
}

/** Due now or past due only (not upcoming within 24h). */
function isFollowUpDueOrPast(
  nextFollowUpAt: string | null | undefined,
  now: Date,
): boolean {
  if (!nextFollowUpAt) return false;
  const dueMs = new Date(nextFollowUpAt).getTime();
  return !Number.isNaN(dueMs) && dueMs <= now.getTime();
}

export function buildCrmRequiredActions(
  opportunities: CrmRequiredActionOpportunity[],
  options: {
    now?: Date;
    noActivity?: CrmNoActivitySettings;
  } = {},
): CrmRequiredActionsGrouped {
  const now = options.now ?? new Date();
  const noActivitySettings = options.noActivity;
  const followUpDue: CrmRequiredActionItem[] = [];
  const newLeads: CrmRequiredActionItem[] = [];
  const noActivity: CrmRequiredActionItem[] = [];

  for (const opp of opportunities) {
    if (opp.status !== 'Open') continue;

    const title = opp.title?.trim() || 'Untitled lead';
    const customerName = opp.customer_name?.trim() || 'Unknown contact';
    const score = typeof opp.lead_score === 'number' ? opp.lead_score : null;
    const scoreFields = score == null ? {} : { leadScore: score, leadScoreReason: opp.lead_score_reason ?? null };

    if (isFollowUpDueOrPast(opp.next_follow_up_at, now)) {
      const due = new Date(opp.next_follow_up_at!);
      const dueMs = due.getTime();
      followUpDue.push({
        kind: 'follow_up_due',
        opportunityId: opp.id,
        title,
        customerName,
        subtitle: `Follow-up due ${formatDistanceToNow(due, { addSuffix: true })}`,
        sortOrder: dueMs,
        dueAt: opp.next_follow_up_at!,
        createdAt: opp.created_at,
        ...scoreFields,
      });
      continue;
    }

    if (opp.is_unread) {
      const created = new Date(opp.created_at);
      newLeads.push({
        kind: 'new_lead',
        opportunityId: opp.id,
        title,
        customerName,
        subtitle: `New lead · ${formatDistanceToNow(created, { addSuffix: true })}`,
        sortOrder: -created.getTime(),
        createdAt: opp.created_at,
        ...scoreFields,
      });
      continue;
    }

    if (
      noActivitySettings?.enabled &&
      !hasScheduledFollowUp(opp.next_follow_up_at, now) &&
      isLeadStaleNoActivity(
        opp.last_activity_at,
        opp.created_at,
        noActivitySettings.hours,
        now,
      )
    ) {
      const lastActivityAt = resolveLastActivityAt(opp.last_activity_at, opp.created_at);
      const lastActivity = new Date(lastActivityAt);
      const lastActivityMs = lastActivity.getTime();
      noActivity.push({
        kind: 'no_activity',
        opportunityId: opp.id,
        title,
        customerName,
        subtitle: `No activity · last ${formatDistanceToNow(lastActivity, { addSuffix: true })}`,
        sortOrder: lastActivityMs,
        createdAt: opp.created_at,
        lastActivityAt,
        ...scoreFields,
      });
    }
  }

  followUpDue.sort((a, b) => a.sortOrder - b.sortOrder);
  newLeads.sort((a, b) => b.sortOrder - a.sortOrder);
  noActivity.sort((a, b) => a.sortOrder - b.sortOrder);

  return mergeRequiredActions({ followUpDue, newLeads, noActivity }, {});
}

/** Millisecond timestamp used to order actions newest → oldest in the inbox. */
export function getRequiredActionTimestamp(item: CrmRequiredActionItem): number {
  if (item.kind === 'no_activity' && item.lastActivityAt) {
    const lastMs = new Date(item.lastActivityAt).getTime();
    if (!Number.isNaN(lastMs)) return lastMs;
  }

  if ((item.kind === 'follow_up_due' || item.kind === 'due_task') && item.dueAt) {
    const dueMs = new Date(item.dueAt).getTime();
    if (!Number.isNaN(dueMs)) return dueMs;
  }

  if (item.kind === 'payroll_request' && !Number.isNaN(item.sortOrder)) {
    return item.sortOrder;
  }

  if (item.kind === 'proposal_convert') {
    const approvedMs = item.sortOrder;
    if (!Number.isNaN(approvedMs) && approvedMs > 0) return approvedMs;
  }

  if (item.kind === 'campaign_billing_failed') {
    const failedMs = item.sortOrder;
    if (!Number.isNaN(failedMs) && failedMs > 0) return failedMs;
  }

  if (item.kind === 'marketing_ai_tokens_blocked') {
    // Keep token blockers at the top of the inbox while the condition is active.
    return Date.now();
  }

  if (item.kind === 'payroll_period' && item.createdAt) {
    const [year, month, day] = item.createdAt.split('T')[0].split('-').map(Number);
    if (year && month && day) {
      return new Date(year, month - 1, day, 23, 59, 59, 999).getTime();
    }
  }

  const createdMs = new Date(item.createdAt).getTime();
  return Number.isNaN(createdMs) ? 0 : createdMs;
}

export function sortRequiredActionsNewestFirst(
  items: CrmRequiredActionItem[],
): CrmRequiredActionItem[] {
  return [...items].sort(
    (a, b) => getRequiredActionTimestamp(b) - getRequiredActionTimestamp(a),
  );
}

export function flattenRequiredActions(
  actions: CrmRequiredActionsGrouped,
): CrmRequiredActionItem[] {
  return sortRequiredActionsNewestFirst([
    ...actions.followUpDue,
    ...actions.dueTasks,
    ...actions.noActivity,
    ...actions.agentCallbacks,
    ...actions.payrollPeriods,
    ...actions.payrollRequests,
    ...actions.proposalConvert,
    ...actions.documentVerifications,
    ...actions.campaignBillingFailed,
    ...actions.marketingAiTokensBlocked,
    ...actions.orderApprovals,
    ...actions.salesPlays,
    ...actions.newLeads,
  ]);
}
